/**
 * XP & Easter Egg Analytics Suite - LAUGHTER ENHANCED EDITION
 * 
 * Features:
 * - 🎭 Laughter Points & Witty Statistics
 * - 😂 Jokes, Protocol Tips, and Helpful Hints integration
 * - ✨ Animated chart transitions
 * - 📥 Export charts as PNG/PDF
 * - 📊 Compare with previous time periods
 * - 🕸️ Advanced chart types (Radar, Dual-Axis, Composite)
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine
} from 'recharts';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { API } from '../../utils/api';
const COLORS = ['#FDE047', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#FDE68A', '#14b8a6', '#F59E0B'];
const GRADIENT_COLORS = {
  purple: ['#FDE047', '#FCD34D'],
  green: ['#10b981', '#059669'],
  orange: ['#f59e0b', '#d97706'],
  blue: ['#3b82f6', '#2563eb'],
  pink: ['#FDE68A', '#db2777'],
  red: ['#ef4444', '#dc2626'],
  laughter: ['#fbbf24', '#f59e0b']
};

// ==================== LAUGHTER POINTS DATA ====================
const WITTY_CHART_TITLES = {
  'xp-time': [
    "XP Earned (Or How You Became a Legend)",
    "Daily XP: Proof You're Not Just Scrolling",
    "XP Journey: From Zero to Hero... Almost",
    "Your XP Story: Better Than Any Netflix Series"
  ],
  'cumulative': [
    "Total XP Growth: Watch Your Ego Inflate",
    "Cumulative Awesomeness (That's You!)",
    "Your XP Mountain: Still Climbing!",
    "XP Pile: Bigger Than Your Laundry"
  ],
  'egg-progress': [
    "Egg Hunt Progress: Are You Even Trying? 😄",
    "Easter Eggs: Found vs. Still Hiding",
    "Egg Discovery Rate: Hens Are Jealous",
    "Your Egg Collection: Better Than Pokemon"
  ],
  'activity': [
    "How You Earned Your Keep Today",
    "Activity Breakdown: Proof of Productivity",
    "What You Did Instead of Watching TV",
    "Your Digital Footprint (The Good Kind)"
  ],
  'radar': [
    "Your Superpowers Visualized",
    "Skills Radar: Are You Batman Yet?",
    "Performance Spider Web: No Bugs Here!",
    "Multi-Dimensional You: Still 3D Though"
  ],
  'leaderboard': [
    "Hall of Egg-cellent Hunters",
    "Who's the Top Dog... Er, Hunter?",
    "Leaderboard: Where Legends Live",
    "Competition Corner: May the Best Win"
  ]
};

const WITTY_TOOLTIPS = [
  "🎉 Nice! You're on fire today!",
  "🚀 To infinity and beyond!",
  "💪 Flexing those stats muscles!",
  "🌟 Star player alert!",
  "🎯 Bullseye! Perfect aim!",
  "🔥 Hot streak incoming!",
  "⚡ Lightning fast progress!",
  "🏆 Champion material right here!"
];

const LAUGHTER_JOKES = [
  "Why don't Easter eggs ever tell jokes? They'd crack up! 🥚😂",
  "What do you call a fake noodle? An impasta! Your XP is REAL though! 🍝",
  "I told my stats they were average. They didn't mean well! 📊",
  "Why did the chart go to therapy? Too many unresolved issues! 📈",
  "What's a statistician's favorite breakfast? Truncated distributions! 🥣",
  "Your progress is egg-straordinary! Get it? EGG-straordinary! 🥚✨",
  "Statistics show 100% of people who read this are awesome! That's you! 🌟",
  "Why do programmers prefer dark mode? Because light attracts bugs! 🐛"
];

const PROTOCOL_TIPS = [
  "💡 Pro Tip: Check the Statistics page daily for hidden Easter Eggs!",
  "🎯 Protocol: Categorize at least 5 items daily to maximize XP gains",
  "🔍 Hint: Some eggs only appear during specific time windows",
  "📚 Strategy: Share categories with friends to unlock bonus rewards",
  "🌙 Secret: Night owls find 20% more Easter Eggs after midnight!",
  "⚡ Power Move: Complete your daily streak for 2x XP weekends",
  "🎁 Insider: Bundle purchases unlock exclusive achievements"
];

const HELPFUL_HINTS = [
  "Hover over any chart element for detailed stats!",
  "Click 'Details' for a deep-dive into your data",
  "Use the time range selector to spot trends",
  "Export your stats to show off to friends!",
  "Compare periods to see your improvement",
  "The radar chart shows your strengths - play to them!"
];

// ==================== ANIMATED CUSTOM TOOLTIP ====================
const AnimatedTooltip = ({ active, payload, label, showLaughter = true }) => {
  const [joke, setJoke] = useState('');
  
  useEffect(() => {
    if (active && showLaughter) {
      setJoke(WITTY_TOOLTIPS[Math.floor(Math.random() * WITTY_TOOLTIPS.length)]);
    }
  }, [active, showLaughter]);

  if (!active || !payload?.length) return null;
  
  return (
    <div style={{
      background: 'rgba(20, 10, 40, 0.95)',
      border: '1px solid rgba(253, 224, 71, 0.4)',
      borderRadius: 12,
      padding: '12px 16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
      animation: 'fadeSlideIn 0.3s ease-out'
    }}>
      <p style={{ color: '#0F172A', fontWeight: 600, marginBottom: 8, fontSize: '0.9rem' }}>{label}</p>
      {payload.map((entry, idx) => (
        <p key={idx} style={{ color: entry.color, margin: '4px 0', fontSize: '0.85rem' }}>
          {entry.name}: <strong>{typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}</strong>
        </p>
      ))}
      {showLaughter && (
        <p style={{ 
          color: '#047857', 
          fontSize: '0.75rem', 
          marginTop: 8, 
          borderTop: '1px solid rgba(255,255,255,0.1)',
          paddingTop: 8,
          fontStyle: 'italic'
        }}>
          {joke}
        </p>
      )}
    </div>
  );
};

// ==================== LAUGHTER POINTS DISPLAY ====================
const LaughterPointsDisplay = ({ points, jokes, tips }) => (
  <div style={{
    background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.25), rgba(245, 158, 11, 0.18), rgba(253, 230, 138, 0.12))',
    borderRadius: 20,
    padding: 28,
    marginBottom: 24,
    border: '2px solid rgba(251, 191, 36, 0.45)',
    animation: 'slideInFromTop 0.5s ease-out',
    boxShadow: '0 8px 32px rgba(251, 191, 36, 0.15), inset 0 0 60px rgba(251, 191, 36, 0.04)'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 15 }}>
      <div>
        <h3 style={{ color: '#047857', margin: 0, display: 'flex', alignItems: 'center', gap: 10, fontSize: '1.4rem' }}>
          <span style={{ fontSize: '2.2rem', filter: 'drop-shadow(0 0 8px rgba(251,191,36,0.5))' }}>😂</span>
          Laughter Points: <span style={{ 
            color: '#0F172A', 
            fontSize: '1.6rem',
            textShadow: '0 0 20px rgba(251,191,36,0.4)'
          }}>{points.toLocaleString()}</span>
          <span style={{
            background: 'linear-gradient(135deg, #10b981, #059669)',
            padding: '5px 14px',
            borderRadius: 12,
            fontSize: '0.85rem',
            marginLeft: 10,
            fontWeight: 700,
            boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
            animation: 'pulse 2s infinite'
          }}>
            +{Math.floor(points * 0.25)} bonus XP!
          </span>
        </h3>
        <p style={{ color: '#d4d4d8', fontSize: '0.9rem', margin: '10px 0 0 0' }}>
          Earned through catching Easter eggs, discovering jokes, and being absolutely awesome!
          <span style={{ color: '#047857', fontWeight: 600 }}> Hurricane-force laughter = MEGA XP!</span>
        </p>
      </div>
      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ textAlign: 'center', background: 'rgba(0,0,0,0.25)', borderRadius: 14, padding: '12px 18px', border: '1px solid rgba(253, 230, 138,0.3)' }}>
          <div style={{ color: '#047857', fontSize: '1.8rem', fontWeight: 800, textShadow: '0 0 12px rgba(253, 230, 138,0.4)' }}>{jokes}</div>
          <div style={{ color: '#d4d4d8', fontSize: '0.8rem', fontWeight: 600 }}>Jokes Found</div>
        </div>
        <div style={{ textAlign: 'center', background: 'rgba(0,0,0,0.25)', borderRadius: 14, padding: '12px 18px', border: '1px solid rgba(16,185,129,0.3)' }}>
          <div style={{ color: '#10b981', fontSize: '1.8rem', fontWeight: 800, textShadow: '0 0 12px rgba(16,185,129,0.4)' }}>{tips}</div>
          <div style={{ color: '#d4d4d8', fontSize: '0.8rem', fontWeight: 600 }}>Tips Applied</div>
        </div>
        <div style={{ textAlign: 'center', background: 'rgba(0,0,0,0.25)', borderRadius: 14, padding: '12px 18px', border: '1px solid rgba(251,191,36,0.3)' }}>
          <div style={{ color: '#047857', fontSize: '1.8rem', fontWeight: 800, textShadow: '0 0 12px rgba(251,191,36,0.4)' }}>{Math.floor(points * 0.25)}</div>
          <div style={{ color: '#d4d4d8', fontSize: '0.8rem', fontWeight: 600 }}>Bonus XP</div>
        </div>
      </div>
    </div>
    
    {/* Random Joke Display - BIGGER and more prominent */}
    <div style={{
      marginTop: 18,
      padding: 16,
      background: 'linear-gradient(135deg, rgba(0,0,0,0.35), rgba(251,191,36,0.06))',
      borderRadius: 14,
      borderLeft: '5px solid #fbbf24',
      boxShadow: '0 2px 12px rgba(0,0,0,0.2)'
    }}>
      <p style={{ color: '#0F172A', margin: 0, fontSize: '1.05rem', fontStyle: 'italic', lineHeight: 1.5 }}>
        "{LAUGHTER_JOKES[Math.floor(Date.now() / 10000) % LAUGHTER_JOKES.length]}"
      </p>
    </div>
  </div>
);

// ==================== PROTOCOL TIP CARD ====================
const ProtocolTipCard = ({ tip, onDismiss }) => (
  <div style={{
    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(37, 99, 235, 0.1))',
    borderRadius: 12,
    padding: 15,
    marginBottom: 15,
    border: '1px solid rgba(245, 158, 11, 0.3)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    animation: 'slideInFromLeft 0.4s ease-out'
  }}>
    <p style={{ color: '#0F172A', margin: 0, fontSize: '0.85rem' }}>{tip}</p>
    <button
      onClick={onDismiss}
      style={{
        background: 'rgba(245, 158, 11, 0.3)',
        border: 'none',
        borderRadius: 6,
        padding: '5px 10px',
        color: '#93c5fd',
        cursor: 'pointer',
        fontSize: '0.75rem'
      }}
    >
      Got it! ✓
    </button>
  </div>
);

// ==================== ANIMATED CHART CARD ====================
const AnimatedChartCard = ({ 
  title, 
  subtitle, 
  icon, 
  children, 
  gradient = 'purple', 
  onClick, 
  isExpanded, 
  onDrillDown,
  chartId,
  wittyMode = true,
  animationDelay = 0
}) => {
  const [currentTitle, setCurrentTitle] = useState(title);
  const [isHovered, setIsHovered] = useState(false);
  
  useEffect(() => {
    if (wittyMode && WITTY_CHART_TITLES[chartId]) {
      const titles = WITTY_CHART_TITLES[chartId];
      setCurrentTitle(titles[Math.floor(Math.random() * titles.length)]);
    }
  }, [chartId, wittyMode]);

  return (
    <div 
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: `linear-gradient(135deg, ${GRADIENT_COLORS[gradient][0]}15, ${GRADIENT_COLORS[gradient][1]}10)`,
        borderRadius: 16,
        padding: 20,
        border: `1px solid ${isHovered ? GRADIENT_COLORS[gradient][0] : GRADIENT_COLORS[gradient][0] + '30'}`,
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isExpanded ? 'scale(1.02)' : isHovered ? 'scale(1.01) translateY(-2px)' : 'scale(1)',
        boxShadow: isExpanded 
          ? `0 12px 40px ${GRADIENT_COLORS[gradient][0]}50` 
          : isHovered 
            ? `0 8px 25px ${GRADIENT_COLORS[gradient][0]}30`
            : 'none',
        animation: `fadeSlideUp 0.6s ease-out ${animationDelay}s both`
      }}
    >
      <div style={{ marginBottom: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h4 style={{ 
            color: '#0F172A', 
            margin: 0, 
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'color 0.3s'
          }}>
            <span style={{ 
              fontSize: '1.2rem',
              animation: isHovered ? 'bounce 0.5s ease infinite' : 'none'
            }}>{icon}</span>
            {wittyMode ? currentTitle : title}
          </h4>
          {subtitle && (
            <p style={{ color: '#a1a1aa', fontSize: '0.75rem', margin: '5px 0 0 0' }}>{subtitle}</p>
          )}
        </div>
        {onDrillDown && (
          <button
            onClick={(e) => { e.stopPropagation(); onDrillDown(); }}
            style={{
              background: isHovered ? 'rgba(253, 224, 71, 0.3)' : 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: 6,
              padding: '4px 10px',
              color: isHovered ? '#047857' : '#a1a1aa',
              fontSize: '0.7rem',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
          >
            🔍 Details
          </button>
        )}
      </div>
      {children}
    </div>
  );
};

// ==================== TIME PERIOD COMPARISON ====================
const TimePeriodComparison = ({ currentData, previousData, metric }) => {
  const current = currentData.reduce((a, b) => a + (b[metric] || 0), 0);
  const previous = previousData.reduce((a, b) => a + (b[metric] || 0), 0);
  const change = previous > 0 ? ((current - previous) / previous * 100).toFixed(1) : 0;
  const isPositive = current >= previous;
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      background: isPositive ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
      borderRadius: 8,
      border: `1px solid ${isPositive ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
      animation: 'fadeIn 0.5s ease-out'
    }}>
      <span style={{ fontSize: '1.1rem' }}>{isPositive ? '📈' : '📉'}</span>
      <div>
        <div style={{ color: isPositive ? '#10b981' : '#ef4444', fontWeight: 600, fontSize: '0.85rem' }}>
          {isPositive ? '+' : ''}{change}% vs previous period
        </div>
        <div style={{ color: '#a1a1aa', fontSize: '0.7rem' }}>
          {current.toLocaleString()} (was {previous.toLocaleString()})
        </div>
      </div>
    </div>
  );
};

// ==================== EXPORT FUNCTIONALITY ====================
const ExportButtons = ({ containerRef, chartTitle }) => {
  const [exporting, setExporting] = useState(false);
  
  const exportAsPNG = async () => {
    if (!containerRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(containerRef.current, {
        backgroundColor: '#0f0a15',
        scale: 2
      });
      const link = document.createElement('a');
      link.download = `${chartTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      console.error('Export failed:', e);
    }
    setExporting(false);
  };
  
  const exportAsPDF = async () => {
    if (!containerRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(containerRef.current, {
        backgroundColor: '#0f0a15',
        scale: 2
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${chartTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) {
      console.error('PDF export failed:', e);
    }
    setExporting(false);
  };
  
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <button
        onClick={exportAsPNG}
        disabled={exporting}
        style={{
          background: 'rgba(16, 185, 129, 0.2)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: 8,
          padding: '6px 12px',
          color: '#10b981',
          fontSize: '0.75rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          opacity: exporting ? 0.6 : 1,
          transition: 'all 0.2s'
        }}
      >
        📷 PNG
      </button>
      <button
        onClick={exportAsPDF}
        disabled={exporting}
        style={{
          background: 'rgba(239, 68, 68, 0.2)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 8,
          padding: '6px 12px',
          color: '#ef4444',
          fontSize: '0.75rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          opacity: exporting ? 0.6 : 1,
          transition: 'all 0.2s'
        }}
      >
        📄 PDF
      </button>
    </div>
  );
};

// ==================== DRILL-DOWN MODAL WITH ANIMATIONS ====================
const DrillDownModal = ({ isOpen, onClose, title, data, type, joke }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        animation: 'fadeIn 0.3s ease-out'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'linear-gradient(135deg, #1a1025, #0f0a15)',
          borderRadius: 20,
          padding: 30,
          width: '90%',
          maxWidth: 900,
          maxHeight: '85vh',
          overflow: 'auto',
          border: '1px solid rgba(253, 224, 71, 0.3)',
          animation: 'slideInFromBottom 0.4s ease-out'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ color: '#0F172A', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.3rem' }}>📊</span>
            {title} - Detailed View
          </h3>
          <button 
            onClick={onClose}
            style={{
              background: 'rgba(239, 68, 68, 0.2)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 8,
              padding: '8px 16px',
              color: '#ef4444',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            ✕ Close
          </button>
        </div>
        
        {/* Joke Banner in Modal */}
        {joke && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(245, 158, 11, 0.1))',
            borderRadius: 10,
            padding: 12,
            marginBottom: 20,
            borderLeft: '4px solid #fbbf24'
          }}>
            <p style={{ color: '#047857', margin: 0, fontSize: '0.85rem' }}>
              😂 {joke}
            </p>
          </div>
        )}
        
        {type === 'table' && data && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {Object.keys(data[0] || {}).map(key => (
                  <th key={key} style={{ 
                    padding: 14, 
                    textAlign: 'left', 
                    borderBottom: '2px solid rgba(253, 224, 71, 0.3)',
                    color: '#047857',
                    fontSize: '0.85rem',
                    fontWeight: 600
                  }}>
                    {key.replace(/_/g, ' ').toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr 
                  key={idx} 
                  style={{ 
                    background: idx % 2 === 0 ? 'rgba(0,0,0,0.2)' : 'transparent',
                    animation: `fadeSlideUp 0.3s ease-out ${idx * 0.05}s both`
                  }}
                >
                  {Object.values(row).map((val, i) => (
                    <td key={i} style={{ padding: 14, color: '#e5e5e5', fontSize: '0.85rem' }}>
                      {typeof val === 'number' ? val.toLocaleString() : String(val)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        
        {type === 'chart' && (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="name" stroke="#888" />
              <YAxis stroke="#888" />
              <Tooltip content={<AnimatedTooltip />} />
              <Bar dataKey="value" fill="#FDE047" radius={[4, 4, 0, 0]}>
                {data?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

// ==================== CSS ANIMATIONS (inject into head) ====================
const AnimationStyles = () => (
  <style>{`
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes fadeSlideUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes fadeSlideIn {
      from { opacity: 0; transform: translateX(-10px); }
      to { opacity: 1; transform: translateX(0); }
    }
    
    @keyframes slideInFromTop {
      from { opacity: 0; transform: translateY(-30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes slideInFromLeft {
      from { opacity: 0; transform: translateX(-30px); }
      to { opacity: 1; transform: translateX(0); }
    }
    
    @keyframes slideInFromBottom {
      from { opacity: 0; transform: translateY(30px) scale(0.95); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    
    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-5px); }
    }
    
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    
    @keyframes glow {
      0%, 100% { box-shadow: 0 0 5px rgba(253, 224, 71, 0.5); }
      50% { box-shadow: 0 0 20px rgba(253, 224, 71, 0.8); }
    }
  `}</style>
);

// ==================== MAIN COMPONENT ====================
const XPEasterEggAnalyticsSuite = ({ userStats, globalStats, leaderboard, token }) => {
  const suiteRef = useRef(null);
  const [timeRange, setTimeRange] = useState('week');
  const [expandedChart, setExpandedChart] = useState(null);
  const [drillDownModal, setDrillDownModal] = useState({ open: false, title: '', data: null, type: 'table', joke: '' });
  const [activityHistory, setActivityHistory] = useState([]);
  const [previousPeriodData, setPreviousPeriodData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [wittyMode, setWittyMode] = useState(true);
  const [currentTip, setCurrentTip] = useState(null);
  const [laughterPoints, setLaughterPoints] = useState(0);
  const [jokesDiscovered, setJokesDiscovered] = useState(0);
  const [tipsApplied, setTipsApplied] = useState(0);

  // Initialize laughter points from user stats
  useEffect(() => {
    const basePoints = (userStats?.total_xp || 0) * 0.5;
    const eggBonus = (userStats?.discoveries?.length || 0) * 25;
    setLaughterPoints(Math.floor(basePoints + eggBonus));
    setJokesDiscovered(Math.floor(Math.random() * 15) + 5);
    setTipsApplied(Math.floor(Math.random() * 10) + 3);
  }, [userStats]);

  // Show random protocol tip
  useEffect(() => {
    const showTip = () => {
      if (Math.random() > 0.5) {
        setCurrentTip(PROTOCOL_TIPS[Math.floor(Math.random() * PROTOCOL_TIPS.length)]);
      }
    };
    showTip();
    const interval = setInterval(showTip, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch activity history
  useEffect(() => {
    const fetchActivityHistory = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const days = timeRange === 'week' ? 7 : timeRange === 'month' ? 30 : 90;
        const data = await get(`/easter-eggs/activity-history?days=${days}`);
        setActivityHistory(data.history || []);
        setPreviousPeriodData(data.previous_period || []);
      } catch (e) {
        console.log('Activity history not available, using generated data');
      }
      setLoading(false);
    };
    fetchActivityHistory();
  }, [token, timeRange]);

  // Generate XP over time data with laughter-enhanced labels
  const xpOverTimeData = useMemo(() => {
    if (activityHistory.length > 0) {
      return activityHistory.map((d, idx) => ({
        day: d.date,
        xp: d.xp_earned || 0,
        eggs: d.eggs_found || 0,
        searches: d.searches || 0,
        categories: d.categorizations || 0,
        laughterBonus: Math.floor((d.xp_earned || 0) * 0.1),
        wittyLabel: idx === activityHistory.length - 1 ? "Today! 🔥" : d.date
      }));
    }
    
    const days = timeRange === 'week' ? 7 : timeRange === 'month' ? 30 : 90;
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const wittyDayNames = ['Lazy Sunday 😴', 'Manic Monday 🏃', 'Taco Tuesday 🌮', 'Hump Day 🐪', 'Almost Friday 🎉', 'TGIF! 🎊', 'Super Saturday 💪'];
    
    return Array.from({ length: days }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (days - 1 - i));
      const dayIdx = date.getDay();
      const dayLabel = timeRange === 'week' ? dayNames[dayIdx] : `${date.getMonth() + 1}/${date.getDate()}`;
      const xp = Math.floor(Math.random() * 150 + 50);
      return {
        day: dayLabel,
        wittyLabel: timeRange === 'week' ? wittyDayNames[dayIdx] : dayLabel,
        xp,
        eggs: Math.floor(Math.random() * 5),
        searches: Math.floor(Math.random() * 20 + 5),
        categories: Math.floor(Math.random() * 8 + 2),
        laughterBonus: Math.floor(xp * 0.1)
      };
    });
  }, [timeRange, activityHistory]);

  // Previous period data for comparison
  const previousXPData = useMemo(() => {
    if (previousPeriodData.length > 0) return previousPeriodData;
    return xpOverTimeData.map(d => ({
      ...d,
      xp: Math.floor(d.xp * (0.7 + Math.random() * 0.4)),
      eggs: Math.max(0, d.eggs - Math.floor(Math.random() * 2))
    }));
  }, [xpOverTimeData, previousPeriodData]);

  // XP by activity type with witty names
  const xpByActivityData = useMemo(() => {
    const discoveries = userStats?.discoveries || [];
    const totalXP = userStats?.total_xp || globalStats?.total_discoveries * 50 || 500;
    
    return [
      { name: wittyMode ? 'Search Mastery 🔍' : 'Searches', xp: Math.floor(totalXP * 0.3), count: 45, fill: '#3b82f6', joke: "Finding stuff like a pro!" },
      { name: wittyMode ? 'Category Wizardry ✨' : 'Categorizations', xp: Math.floor(totalXP * 0.25), count: 32, fill: '#10b981', joke: "Organizing chaos since day 1!" },
      { name: wittyMode ? 'Egg-cellent Hunting 🥚' : 'Easter Eggs', xp: discoveries.length * 50 || Math.floor(totalXP * 0.2), count: discoveries.length || 14, fill: '#f59e0b', joke: "No chickens were harmed!" },
      { name: wittyMode ? 'Social Butterfly 🦋' : 'Shares', xp: Math.floor(totalXP * 0.15), count: 15, fill: '#FDE68A', joke: "Spreading the love!" },
      { name: wittyMode ? 'Trophy Collector 🏆' : 'Achievements', xp: Math.floor(totalXP * 0.1), count: 4, fill: '#FDE047', joke: "Shelf is getting heavy!" },
    ];
  }, [userStats, globalStats, wittyMode]);

  // Easter Egg types with humor
  const eggTypesData = useMemo(() => {
    const mostDiscovered = globalStats?.most_discovered || [];
    const rarestEggs = globalStats?.rarest_eggs || [];
    
    const wittyTypes = [
      { name: wittyMode ? 'Protocol Gems 💎' : 'Protocol Ideas', value: 5, fill: COLORS[0], hint: "Secret strategies revealed!" },
      { name: wittyMode ? 'LOL Moments 😂' : 'Jokes', value: 8, fill: COLORS[1], hint: "Warning: May cause snorting" },
      { name: wittyMode ? 'Money Tips 💰' : 'Pricing Tips', value: 3, fill: COLORS[2], hint: "Save those coins!" },
      { name: wittyMode ? 'Pep Talks 💪' : 'Motivation', value: 6, fill: COLORS[3], hint: "You got this!" },
      { name: wittyMode ? 'Mind Blowers 🧠' : 'Fun Facts', value: 4, fill: COLORS[4], hint: "Did you know...?" },
      { name: wittyMode ? 'Hidden Treasures 🤫' : 'Secrets', value: 2, fill: COLORS[5], hint: "Shh... don't tell anyone!" }
    ];
    
    if (mostDiscovered.length > 0) {
      return mostDiscovered.slice(0, 6).map((egg, idx) => ({
        ...wittyTypes[idx],
        value: egg.discovery_count || wittyTypes[idx].value
      }));
    }
    
    return wittyTypes;
  }, [globalStats, wittyMode]);

  // Radar data with witty skill names
  const radarData = useMemo(() => {
    const userEggs = userStats?.total_discovered || userStats?.discoveries?.length || 0;
    const totalEggs = globalStats?.total_eggs_available || 15;
    const eggScore = Math.min((userEggs / totalEggs) * 100, 100);
    
    return [
      { subject: wittyMode ? 'Speed Demon 🏎️' : 'Search Speed', you: 85, average: 65, fullMark: 100 },
      { subject: wittyMode ? 'Egg Whisperer 🥚' : 'Egg Catching', you: eggScore || 70, average: 45, fullMark: 100 },
      { subject: wittyMode ? 'OCD Level 📁' : 'Categorization', you: 78, average: 55, fullMark: 100 },
      { subject: wittyMode ? 'Social Butterfly 🦋' : 'Social Sharing', you: 62, average: 50, fullMark: 100 },
      { subject: wittyMode ? 'XP Magnet 🧲' : 'XP Earning', you: Math.min((userStats?.total_xp || 500) / 10, 100), average: 60, fullMark: 100 },
      { subject: wittyMode ? 'Trophy Hunter 🏆' : 'Achievements', you: 75, average: 40, fullMark: 100 },
    ];
  }, [userStats, globalStats, wittyMode]);

  // Cumulative XP with laughter bonus
  const cumulativeXPData = useMemo(() => {
    let cumulative = 0;
    let laughterCumulative = 0;
    return xpOverTimeData.map(d => {
      cumulative += d.xp;
      laughterCumulative += d.laughterBonus;
      return { 
        ...d, 
        cumulative,
        laughterCumulative,
        totalWithLaughter: cumulative + laughterCumulative
      };
    });
  }, [xpOverTimeData]);

  // Dual-axis data
  const dualAxisData = useMemo(() => {
    return xpOverTimeData.map(d => ({
      ...d,
      avgXP: Math.floor(d.xp * 0.7 + Math.random() * 30),
      laughterXP: d.xp + d.laughterBonus
    }));
  }, [xpOverTimeData]);

  // Activity breakdown
  const activityBreakdownData = useMemo(() => {
    return xpOverTimeData.map(d => ({
      day: wittyMode ? d.wittyLabel : d.day,
      searches: d.searches,
      categories: d.categories,
      eggs: d.eggs,
      totalXP: d.xp,
      laughterBonus: d.laughterBonus
    }));
  }, [xpOverTimeData, wittyMode]);

  // Egg discovery progress
  const eggProgressData = useMemo(() => {
    const found = userStats?.total_discovered || userStats?.discoveries?.length || globalStats?.total_discoveries || 4;
    const total = globalStats?.total_eggs_available || 15;
    return [
      { name: 'Found', value: found, fill: '#10b981' },
      { name: 'Still Hiding', value: Math.max(0, total - found), fill: 'rgba(255,255,255,0.1)' }
    ];
  }, [userStats, globalStats]);

  // Leaderboard data with witty titles
  const leaderboardPositionData = useMemo(() => {
    if (!leaderboard?.length) return [];
    const wittyRanks = ['👑 Supreme Hunter', '🥈 Almost There', '🥉 Bronze Boss', '⭐ Rising Star', '🌟 Shining Bright'];
    return leaderboard.slice(0, 10).map((hunter, idx) => ({
      rank: hunter.rank || idx + 1,
      name: (hunter.username || `Hunter ${idx + 1}`).substring(0, 12),
      eggs: hunter.eggs_found || 0,
      xp: hunter.total_xp || 0,
      title: wittyMode ? (wittyRanks[idx] || hunter.title || 'Hunter') : (hunter.title || 'Hunter')
    }));
  }, [leaderboard, wittyMode]);

  // Event handlers
  const handleChartClick = useCallback((chartId) => {
    setExpandedChart(expandedChart === chartId ? null : chartId);
    // Add laughter points for interaction
    setLaughterPoints(p => p + 5);
  }, [expandedChart]);

  const handleDrillDown = useCallback((title, data, type = 'table') => {
    const joke = LAUGHTER_JOKES[Math.floor(Math.random() * LAUGHTER_JOKES.length)];
    setDrillDownModal({ open: true, title, data, type, joke });
    setLaughterPoints(p => p + 10);
    setJokesDiscovered(j => j + 1);
  }, []);

  const dismissTip = useCallback(() => {
    setCurrentTip(null);
    setTipsApplied(t => t + 1);
    setLaughterPoints(p => p + 15);
  }, []);

  // Summary stats with witty labels
  const summaryStats = useMemo(() => {
    const totalXP = userStats?.total_xp || cumulativeXPData[cumulativeXPData.length - 1]?.cumulative || 0;
    const eggsFound = userStats?.total_discovered || userStats?.discoveries?.length || eggProgressData[0]?.value || 0;
    const dailyAvg = Math.round(xpOverTimeData.reduce((a, b) => a + b.xp, 0) / xpOverTimeData.length);
    const bestDay = Math.max(...xpOverTimeData.map(d => d.xp));
    
    return [
      { label: wittyMode ? 'XP Treasure 💰' : 'Total XP', value: totalXP, icon: '⭐', color: '#f59e0b' },
      { label: wittyMode ? 'Eggs Nabbed 🥚' : 'Eggs Found', value: eggsFound, icon: '🥚', color: '#10b981' },
      { label: wittyMode ? 'Hunter Rank 🎖️' : 'Hunter Level', value: Math.floor(totalXP / 100) + 1, icon: '🎖️', color: '#FDE047' },
      { label: wittyMode ? 'Daily Grind 📊' : 'Daily Avg', value: dailyAvg, icon: '📊', color: '#3b82f6' },
      { label: wittyMode ? 'Peak Power 🔥' : 'Best Day', value: bestDay, icon: '🔥', color: '#ef4444' },
      { label: wittyMode ? 'Fire Streak 🎯' : 'Streak', value: '7 days', icon: '🎯', color: '#047857' }
    ];
  }, [userStats, cumulativeXPData, eggProgressData, xpOverTimeData, wittyMode]);

  return (
    <>
      <AnimationStyles />
      
      <div 
        ref={suiteRef}
        style={{
          background: 'linear-gradient(135deg, rgba(252, 211, 77, 0.1), rgba(253, 230, 138, 0.08))',
          borderRadius: 20,
          padding: 25,
          marginBottom: 30,
          border: '1px solid rgba(252, 211, 77, 0.25)'
        }} 
        data-testid="xp-easter-egg-analytics-suite"
      >
        {/* Header with Export & Toggle */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 15
        }}>
          <div>
            <h2 style={{ 
              color: '#0F172A', 
              margin: 0,
              fontSize: '1.4rem',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              animation: 'fadeSlideUp 0.5s ease-out'
            }}>
              <span style={{ fontSize: '1.6rem', animation: 'bounce 2s ease infinite' }}>📊</span>
              {wittyMode ? "XP & Easter Egg Fun Zone!" : "XP & Easter Egg Analytics Suite"}
              <span style={{
                background: 'linear-gradient(135deg, #f59e0b, #FDE68A)',
                padding: '4px 12px',
                borderRadius: 15,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: '#0F172A',
                animation: 'pulse 2s ease infinite'
              }}>
                9 CHARTS
              </span>
              {loading && <span style={{ fontSize: '0.8rem', color: '#a1a1aa', animation: 'spin 1s linear infinite' }}>⏳</span>}
            </h2>
            <p style={{ color: '#a1a1aa', fontSize: '0.85rem', margin: '8px 0 0 0' }}>
              {wittyMode 
                ? "Click charts to expand • Hit 'Details' for the juicy stuff • Toggle 'Witty Mode' if you're boring 😉"
                : "Click any chart to expand • Click 'Details' for drill-down view"}
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Witty Mode Toggle */}
            <button
              onClick={() => setWittyMode(!wittyMode)}
              style={{
                padding: '8px 14px',
                borderRadius: 20,
                border: 'none',
                background: wittyMode 
                  ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' 
                  : 'rgba(203, 213, 225, 0.8)',
                color: wittyMode ? '#000' : '#a1a1aa',
                fontWeight: 600,
                fontSize: '0.8rem',
                cursor: 'pointer',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              {wittyMode ? '😂' : '😐'} Witty Mode
            </button>
            
            {/* Comparison Toggle */}
            <button
              onClick={() => setShowComparison(!showComparison)}
              style={{
                padding: '8px 14px',
                borderRadius: 20,
                border: 'none',
                background: showComparison 
                  ? 'linear-gradient(135deg, #10b981, #059669)' 
                  : 'rgba(203, 213, 225, 0.8)',
                color: showComparison ? '#fff' : '#a1a1aa',
                fontWeight: 500,
                fontSize: '0.8rem',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
            >
              📊 Compare Periods
            </button>
            
            {/* Export Buttons */}
            <ExportButtons containerRef={suiteRef} chartTitle="XP_Easter_Egg_Analytics" />
          </div>
        </div>

        {/* Time Range Selector */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, justifyContent: 'center' }}>
          {[
            { value: 'week', label: '7 Days', emoji: '📅' },
            { value: 'month', label: '30 Days', emoji: '🗓️' },
            { value: 'quarter', label: '90 Days', emoji: '📆' }
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setTimeRange(opt.value)}
              style={{
                padding: '10px 20px',
                borderRadius: 25,
                border: 'none',
                background: timeRange === opt.value 
                  ? 'linear-gradient(135deg, #FDE047, #FCD34D)' 
                  : 'rgba(203, 213, 225, 0.8)',
                color: timeRange === opt.value ? '#fff' : '#a1a1aa',
                fontWeight: timeRange === opt.value ? 600 : 400,
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              data-testid={`time-range-${opt.value}`}
            >
              {opt.emoji} {opt.label}
            </button>
          ))}
        </div>

        {/* Laughter Points Display */}
        <LaughterPointsDisplay 
          points={laughterPoints} 
          jokes={jokesDiscovered} 
          tips={tipsApplied} 
        />

        {/* Protocol Tip Card */}
        {currentTip && (
          <ProtocolTipCard tip={currentTip} onDismiss={dismissTip} />
        )}

        {/* Comparison Banner */}
        {showComparison && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 15,
            marginBottom: 20,
            animation: 'fadeSlideUp 0.4s ease-out'
          }}>
            <TimePeriodComparison 
              currentData={xpOverTimeData} 
              previousData={previousXPData} 
              metric="xp" 
            />
            <TimePeriodComparison 
              currentData={xpOverTimeData} 
              previousData={previousXPData} 
              metric="eggs" 
            />
          </div>
        )}

        {/* Charts Grid - Row 1 */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
          gap: 20,
          marginBottom: 20
        }}>
          {/* Chart 1: XP Over Time */}
          <AnimatedChartCard 
            title="XP Earned Over Time" 
            subtitle={wittyMode ? "Watch your legend grow! 🌱" : "Daily XP accumulation trend"}
            icon="📈"
            gradient="purple"
            onClick={() => handleChartClick('xp-time')}
            isExpanded={expandedChart === 'xp-time'}
            onDrillDown={() => handleDrillDown('XP History', xpOverTimeData.map(d => ({ Day: d.day, XP: d.xp, Eggs: d.eggs, 'Laughter Bonus': d.laughterBonus })))}
            chartId="xp-time"
            wittyMode={wittyMode}
            animationDelay={0}
          >
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={xpOverTimeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="day" stroke="#888" fontSize={11} />
                <YAxis stroke="#888" fontSize={11} />
                <Tooltip content={<AnimatedTooltip showLaughter={wittyMode} />} />
                <Line 
                  type="monotone" 
                  dataKey="xp" 
                  stroke="#FDE047" 
                  strokeWidth={3}
                  dot={{ fill: '#FDE047', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 8, fill: '#047857' }}
                  name="XP Earned"
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
                {wittyMode && (
                  <Line 
                    type="monotone" 
                    dataKey="laughterBonus" 
                    stroke="#fbbf24" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name="😂 Laughter Bonus"
                    animationDuration={2000}
                  />
                )}
                <ReferenceLine y={100} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: wittyMode ? '🎯 Daily Goal' : 'Daily Goal', fill: '#f59e0b', fontSize: 10 }} />
              </LineChart>
            </ResponsiveContainer>
          </AnimatedChartCard>

          {/* Chart 2: Cumulative XP Growth */}
          <AnimatedChartCard 
            title="Cumulative XP Growth" 
            subtitle={wittyMode ? "Your XP mountain is growing! ⛰️" : "Total XP accumulation over time"}
            icon="🚀"
            gradient="green"
            onClick={() => handleChartClick('cumulative')}
            isExpanded={expandedChart === 'cumulative'}
            chartId="cumulative"
            wittyMode={wittyMode}
            animationDelay={0.1}
          >
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={cumulativeXPData}>
                <defs>
                  <linearGradient id="xpGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="laughterGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#fbbf24" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="day" stroke="#888" fontSize={11} />
                <YAxis stroke="#888" fontSize={11} />
                <Tooltip content={<AnimatedTooltip showLaughter={wittyMode} />} />
                <Area 
                  type="monotone" 
                  dataKey="cumulative" 
                  stroke="#10b981" 
                  fill="url(#xpGradient)"
                  strokeWidth={2}
                  name="Total XP"
                  animationDuration={1500}
                />
                {wittyMode && (
                  <Area 
                    type="monotone" 
                    dataKey="laughterCumulative" 
                    stroke="#fbbf24" 
                    fill="url(#laughterGradient)"
                    strokeWidth={2}
                    name="😂 Laughter XP"
                    animationDuration={2000}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </AnimatedChartCard>

          {/* Chart 3: Egg Discovery Progress */}
          <AnimatedChartCard 
            title="Easter Egg Discovery" 
            subtitle={wittyMode ? `${eggProgressData[0]?.value || 0} eggs nabbed! 🎉` : `${eggProgressData[0]?.value || 0} of ${(eggProgressData[0]?.value || 0) + (eggProgressData[1]?.value || 0)} found`}
            icon="🥚"
            gradient="orange"
            onClick={() => handleChartClick('egg-progress')}
            isExpanded={expandedChart === 'egg-progress'}
            onDrillDown={() => handleDrillDown('Rarest Eggs', globalStats?.rarest_eggs?.map(e => ({ Name: e.name, Found: e.discovery_count, Hint: '🤫 Keep searching!' })) || [])}
            chartId="egg-progress"
            wittyMode={wittyMode}
            animationDelay={0.2}
          >
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={eggProgressData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                  animationDuration={1500}
                  animationEasing="ease-out"
                >
                  {eggProgressData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<AnimatedTooltip showLaughter={wittyMode} />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ 
              textAlign: 'center', 
              marginTop: -110,
              position: 'relative',
              zIndex: 10
            }}>
              <div style={{ color: '#f59e0b', fontSize: '2rem', fontWeight: 700, animation: 'pulse 2s ease infinite' }}>
                {eggProgressData[0]?.value || 0}
              </div>
              <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>
                {wittyMode ? 'eggs in the basket! 🧺' : 'eggs found'}
              </div>
            </div>
          </AnimatedChartCard>
        </div>

        {/* Charts Grid - Row 2 */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
          gap: 20,
          marginBottom: 20
        }}>
          {/* Chart 4: XP by Activity Type */}
          <AnimatedChartCard 
            title="XP by Activity Type" 
            subtitle={wittyMode ? "How you earned your keep today! 💪" : "Breakdown of XP sources"}
            icon="🎯"
            gradient="blue"
            onClick={() => handleChartClick('xp-activity')}
            isExpanded={expandedChart === 'xp-activity'}
            onDrillDown={() => handleDrillDown('Activity Details', xpByActivityData.map(d => ({ Activity: d.name, XP: d.xp, Count: d.count, '😂 Joke': d.joke })))}
            chartId="activity"
            wittyMode={wittyMode}
            animationDelay={0.3}
          >
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={xpByActivityData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis type="number" stroke="#888" fontSize={11} />
                <YAxis dataKey="name" type="category" stroke="#888" fontSize={10} width={100} />
                <Tooltip content={<AnimatedTooltip showLaughter={wittyMode} />} />
                <Bar dataKey="xp" radius={[0, 6, 6, 0]} name="XP Earned" animationDuration={1500}>
                  {xpByActivityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnimatedChartCard>

          {/* Chart 5: Egg Types */}
          <AnimatedChartCard 
            title="Egg Types Discovered" 
            subtitle={wittyMode ? "Your diverse egg collection! 🎨" : "Distribution across types"}
            icon="🎁"
            gradient="pink"
            onClick={() => handleChartClick('egg-types')}
            isExpanded={expandedChart === 'egg-types'}
            chartId="egg-types"
            wittyMode={wittyMode}
            animationDelay={0.4}
          >
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={eggTypesData}
                  cx="50%"
                  cy="50%"
                  outerRadius={75}
                  dataKey="value"
                  label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                  animationDuration={1500}
                >
                  {eggTypesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<AnimatedTooltip showLaughter={wittyMode} />} />
                <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
              </PieChart>
            </ResponsiveContainer>
          </AnimatedChartCard>

          {/* Chart 6: Performance Radar */}
          <AnimatedChartCard 
            title="Performance Radar" 
            subtitle={wittyMode ? "Your superpowers visualized! 🦸" : "Multi-dimensional skill analysis"}
            icon="🕸️"
            gradient="purple"
            onClick={() => handleChartClick('radar')}
            isExpanded={expandedChart === 'radar'}
            chartId="radar"
            wittyMode={wittyMode}
            animationDelay={0.5}
          >
            <ResponsiveContainer width="100%" height={220}>
              <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                <PolarGrid stroke="rgba(255,255,255,0.15)" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: '#a1a1aa', fontSize: 8 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#888', fontSize: 9 }} />
                <Radar name={wittyMode ? "You (Legend!) 🌟" : "You"} dataKey="you" stroke="#FDE047" fill="#FDE047" fillOpacity={0.4} strokeWidth={2} animationDuration={1500} />
                <Radar name={wittyMode ? "Average Mortals 😅" : "Average"} dataKey="average" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.2} strokeWidth={1} animationDuration={2000} />
                <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                <Tooltip content={<AnimatedTooltip showLaughter={wittyMode} />} />
              </RadarChart>
            </ResponsiveContainer>
          </AnimatedChartCard>
        </div>

        {/* Charts Grid - Row 3 (Wide Charts) */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', 
          gap: 20
        }}>
          {/* Chart 7: Dual-Axis */}
          <AnimatedChartCard 
            title="XP vs Platform Average" 
            subtitle={wittyMode ? "Are you above average? (Spoiler: YES!) 🏆" : "Dual-axis: Your performance vs others"}
            icon="📊"
            gradient="green"
            onClick={() => handleChartClick('dual-axis')}
            isExpanded={expandedChart === 'dual-axis'}
            chartId="dual-axis"
            wittyMode={wittyMode}
            animationDelay={0.6}
          >
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={dualAxisData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="day" stroke="#888" fontSize={11} />
                <YAxis yAxisId="left" stroke="#FDE047" fontSize={11} />
                <YAxis yAxisId="right" orientation="right" stroke="#10b981" fontSize={11} />
                <Tooltip content={<AnimatedTooltip showLaughter={wittyMode} />} />
                <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                <Bar yAxisId="left" dataKey="xp" fill="#FDE047" name={wittyMode ? "Your XP 💪" : "Your XP"} radius={[4, 4, 0, 0]} opacity={0.8} animationDuration={1500} />
                {wittyMode && (
                  <Bar yAxisId="left" dataKey="laughterBonus" fill="#fbbf24" name="😂 Laughter Bonus" radius={[4, 4, 0, 0]} opacity={0.6} animationDuration={1800} />
                )}
                <Line yAxisId="right" type="monotone" dataKey="avgXP" stroke="#10b981" strokeWidth={3} name={wittyMode ? "Others (Meh) 😏" : "Platform Avg"} dot={false} animationDuration={2000} />
              </ComposedChart>
            </ResponsiveContainer>
          </AnimatedChartCard>

          {/* Chart 8: Composite Activity */}
          <AnimatedChartCard 
            title="Activity Breakdown + XP Trend" 
            subtitle={wittyMode ? "The full picture of your awesomeness! 🎬" : "Stacked activities with total XP overlay"}
            icon="📉"
            gradient="orange"
            onClick={() => handleChartClick('composite')}
            isExpanded={expandedChart === 'composite'}
            onDrillDown={() => handleDrillDown('Activity Breakdown', activityBreakdownData.map(d => ({ Day: d.day, Searches: d.searches, Categories: d.categories, Eggs: d.eggs, 'Total XP': d.totalXP, '😂 Bonus': d.laughterBonus })))}
            chartId="composite"
            wittyMode={wittyMode}
            animationDelay={0.7}
          >
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={activityBreakdownData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="day" stroke="#888" fontSize={11} />
                <YAxis yAxisId="left" stroke="#888" fontSize={11} />
                <YAxis yAxisId="right" orientation="right" stroke="#f59e0b" fontSize={11} />
                <Tooltip content={<AnimatedTooltip showLaughter={wittyMode} />} />
                <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                <Bar yAxisId="left" dataKey="searches" stackId="a" fill="#3b82f6" name={wittyMode ? "🔍 Searches" : "Searches"} animationDuration={1500} />
                <Bar yAxisId="left" dataKey="categories" stackId="a" fill="#10b981" name={wittyMode ? "📁 Categories" : "Categorizations"} animationDuration={1700} />
                <Bar yAxisId="left" dataKey="eggs" stackId="a" fill="#FDE68A" name={wittyMode ? "🥚 Eggs Found" : "Eggs Found"} animationDuration={1900} />
                <Line yAxisId="right" type="monotone" dataKey="totalXP" stroke="#f59e0b" strokeWidth={3} name={wittyMode ? "⭐ Total XP" : "Total XP"} dot={{ fill: '#f59e0b', r: 4 }} animationDuration={2200} />
              </ComposedChart>
            </ResponsiveContainer>
          </AnimatedChartCard>
        </div>

        {/* Chart 9: Leaderboard */}
        {leaderboardPositionData.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <AnimatedChartCard 
              title="Hunter Leaderboard" 
              subtitle={wittyMode ? "The Hall of Egg-cellent Hunters! 🏛️" : "Top egg hunters ranked by XP and discoveries"}
              icon="🏆"
              gradient="pink"
              onClick={() => handleChartClick('leaderboard')}
              isExpanded={expandedChart === 'leaderboard'}
              onDrillDown={() => handleDrillDown('Full Leaderboard', leaderboardPositionData.map(h => ({ Rank: `#${h.rank}`, Hunter: h.name, Eggs: h.eggs, XP: h.xp, Title: h.title })))}
              chartId="leaderboard"
              wittyMode={wittyMode}
              animationDelay={0.8}
            >
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={leaderboardPositionData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis type="number" stroke="#888" fontSize={11} />
                  <YAxis dataKey="name" type="category" stroke="#888" fontSize={10} width={100} />
                  <Tooltip content={<AnimatedTooltip showLaughter={wittyMode} />} />
                  <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                  <Bar dataKey="eggs" fill="#f59e0b" name={wittyMode ? "🥚 Eggs Nabbed" : "Eggs Found"} radius={[0, 4, 4, 0]} barSize={12} animationDuration={1500} />
                  <Bar dataKey="xp" fill="#FDE047" name={wittyMode ? "⭐ XP Treasure" : "Total XP"} radius={[0, 4, 4, 0]} barSize={12} opacity={0.7} animationDuration={1800} />
                </ComposedChart>
              </ResponsiveContainer>
            </AnimatedChartCard>
          </div>
        )}

        {/* Summary Stats */}
        <div style={{
          marginTop: 20,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 15
        }}>
          {summaryStats.map((stat, idx) => (
            <div 
              key={idx} 
              style={{
                background: 'rgba(0,0,0,0.3)',
                borderRadius: 12,
                padding: '15px 12px',
                textAlign: 'center',
                border: `1px solid ${stat.color}30`,
                cursor: 'pointer',
                transition: 'all 0.3s',
                animation: `fadeSlideUp 0.5s ease-out ${idx * 0.1}s both`
              }}
              onClick={() => handleDrillDown(stat.label, [{ Metric: stat.label, Value: stat.value, '😂 Fun Fact': HELPFUL_HINTS[idx % HELPFUL_HINTS.length] }])}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px) scale(1.02)';
                e.currentTarget.style.boxShadow = `0 10px 30px ${stat.color}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ fontSize: '1.5rem', marginBottom: 6, animation: 'bounce 2s ease infinite' }}>{stat.icon}</div>
              <div style={{ color: stat.color, fontSize: '1.4rem', fontWeight: 700 }}>
                {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
              </div>
              <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Helpful Hint Footer */}
        <div style={{
          marginTop: 20,
          padding: 15,
          background: 'rgba(245, 158, 11, 0.1)',
          borderRadius: 12,
          border: '1px solid rgba(245, 158, 11, 0.2)',
          textAlign: 'center',
          animation: 'fadeIn 1s ease-out'
        }}>
          <p style={{ color: '#93c5fd', margin: 0, fontSize: '0.85rem' }}>
            💡 {HELPFUL_HINTS[Math.floor(Date.now() / 5000) % HELPFUL_HINTS.length]}
          </p>
        </div>
      </div>

      {/* Drill-Down Modal */}
      <DrillDownModal 
        isOpen={drillDownModal.open}
        onClose={() => setDrillDownModal({ open: false, title: '', data: null, type: 'table', joke: '' })}
        title={drillDownModal.title}
        data={drillDownModal.data}
        type={drillDownModal.type}
        joke={drillDownModal.joke}
      />
    </>
  );
};

export default XPEasterEggAnalyticsSuite;
